/**
 * Login-Hinweis auf den Umzugs-Assistenten (Migrationsassistent).
 *
 * Zeigt Usern bei jedem Login einen modernen, CTA-starken Hinweis, wo sie
 * den Umzugs-Assistenten finden (Einstellungen → Umzug) — solange:
 *   - das Onboarding bereits abgeschlossen ist (nicht über dem Wizard einblenden), UND
 *   - die Migration noch NICHT erstmalig abgeschlossen wurde (Backend-Status), UND
 *   - der User nicht „nicht mehr anzeigen" gewählt hat (localStorage opt-out).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Truck, ArrowRight, Check, Sparkles } from 'lucide-react';

const OPT_OUT_KEY = 'ivoliq_migration_hint_optout';
// Juni 2026 — nur 1× pro Session/Login zeigen (nicht bei jedem Reload).
// sessionStorage überlebt Reloads, wird aber bei neuer Browser-Session geleert;
// bei Logout (user == null) wird der Flag aktiv zurückgesetzt → nächster Login zeigt es erneut.
const SESSION_SHOWN_KEY = 'ivoliq_migration_hint_shown';
// Juli 2026 (User-Feedback „zu aufdringlich“) — Auto-Anzeige max. 2× (1. und 3. Session),
// danach nur noch der dauerhafte Einstieg unter Einstellungen → Umzug.
const SHOW_COUNT_KEY = 'ivoliq_migration_hint_show_count';

const FEATURES = ['Kunden', 'Rechnungen', 'Belege'];

export default function MigrationHintModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user) { try { sessionStorage.removeItem(SESSION_SHOWN_KEY); } catch { /* noop */ } return; }
      if (!user.onboarding_completed) return;   // nicht während des Onboardings einblenden
      if (localStorage.getItem(OPT_OUT_KEY) === '1') return;
      if (sessionStorage.getItem(SESSION_SHOWN_KEY) === '1') return;   // diese Session bereits gezeigt
      // Overlay-Priorität (Juni 2026, Paket A): max. 1 Overlay gleichzeitig.
      // 1) Cookie-Banner zuerst entscheiden lassen, 2) AVV-Banner hat Vorrang.
      if (!localStorage.getItem('ivoliq_cookie_consent')) return;
      const avvPending = !(Boolean(user.avv_accepted_at) && user.avv_version === '2.0');
      if (avvPending) return;
      try {
        const res = await api.get('/migration/status');
        if (res.data?.status === 'completed') return;
      } catch (e) {
        if (e.response && e.response.status !== 404) return;
      }
      if (!cancelled) {
        // Juli 2026 — Session-Zähler: nur bei der 1. und 3. Gelegenheit anzeigen
        let n = 0;
        try { n = parseInt(localStorage.getItem(SHOW_COUNT_KEY) || '0', 10) || 0; } catch { /* noop */ }
        try { localStorage.setItem(SHOW_COUNT_KEY, String(n + 1)); } catch { /* noop */ }
        try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch { /* noop */ }
        if (n !== 0 && n !== 2) return;
        setOpen(true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user]);

  const goToMigration = () => {
    setOpen(false);
    navigate('/settings?tab=migration');
  };

  const dontShowAgain = () => {
    localStorage.setItem(OPT_OUT_KEY, '1');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden border-0 sm:max-w-[440px] rounded-2xl shadow-2xl"
        data-testid="migration-hint-modal"
      >
        {/* Barrierefreiheit: für Screenreader (visuell ausgeblendet) */}
        <DialogTitle className="sr-only">Umzugs-Assistent: Schon umgezogen?</DialogTitle>
        <DialogDescription className="sr-only">
          Hol deine Daten aus sevDesk, Lexware Office, DATEV und Co. geführt und GoBD-konform zu ivoliq.
        </DialogDescription>
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-600 to-teal-700 px-7 pt-8 pb-12 text-center">
          <div className="absolute inset-0 opacity-[0.12]"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
          <div className="relative inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
            <Sparkles className="h-3 w-3" /> Umzugs-Assistent
          </div>
          <div className="relative mx-auto mt-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-lg">
            <Truck className="h-8 w-8 text-emerald-600" />
          </div>
        </div>

        {/* Body — überlappt den Hero leicht für moderne Optik */}
        <div className="-mt-6 rounded-t-2xl bg-white px-7 pb-7 pt-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Schon umgezogen?</h2>
          <p className="mx-auto mt-2 max-w-[340px] text-sm leading-relaxed text-slate-500">
            Hol deine Daten aus sevDesk, Lexware Office, DATEV &amp; Co. in wenigen Minuten
            zu ivoliq — geführt, geprüft, GoBD-konform.
          </p>

          <div className="mt-5 flex items-center justify-center gap-2">
            {FEATURES.map((f) => (
              <span key={f}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700"
                data-testid={`migration-hint-feature-${f.toLowerCase()}`}>
                <Check className="h-3.5 w-3.5" /> {f}
              </span>
            ))}
          </div>

          <Button
            onClick={goToMigration}
            className="mt-7 h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold text-white shadow-lg shadow-emerald-600/25 transition-all hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-emerald-600/40"
            data-testid="migration-hint-cta"
          >
            Jetzt umziehen <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <button
            onClick={dontShowAgain}
            className="mt-3 w-full text-xs font-medium text-slate-400 transition-colors hover:text-slate-600"
            data-testid="migration-hint-optout"
          >
            Diesen Hinweis nicht mehr anzeigen
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
